import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PurchasedTickets.scss';
import PurchasedTicketsList from './components/PurchasedTicketsList';
import {
  calculateTicketRefund, getActiveTickets,
  getTicketsHistory,
} from '../../../../../../services/customer/ticketServices';
import TicketRefundModal from '../../../../components/modals/ticketRefundModal/TicketRefundModal';
import handleDownloadTickets from '../../../../../../utils/functions/handleDownloadTickets';
import { useModal } from '../../../../../contexts/modalsContext/modalsContext';
import TicketAdditionalServicesModal from '../../../../components/modals/ticketAdditionalServices/TicketAdditionalServicesModal';
import useTranslation from '../../../../../hooks/translation/useTranslation';
import RefundConfirmationModal from '../../../../components/modals/refundConfirmationModal/RefundConfirmationModal';
import DefaultPagination from '../../../../components/paginations/defaultPagination/DefaultPagination';

function PurchasedTickets() {
  const [activeTickets, setActiveTickets] = useState({});
  const [historyTickets, setHistoryTickets] = useState({});
  const { trans } = useTranslation();
  const [refundModal, setRefundModal] = useState({
    show: false,
    data: null,
  });
  const [searchParams] = useSearchParams();
  const { setShowTicketAdditionalServicesModal } = useModal();

  const { setShowErrorModal } = useModal();

  const returnGroupedTickets = (resTickets = []) => {
    const groupedTickets = {};
    resTickets?.forEach((ticket) => {
      const groupKey = `${ticket.statement.statement_id}_${ticket.ride_id}`;
      if (!groupedTickets[groupKey]) {
        groupedTickets[groupKey] = [];
      }
      groupedTickets[groupKey].push(ticket);
    });
    return groupedTickets;
  };

  const fetchActiveTickets = async (page = 1) => {
    const res = await getActiveTickets({ page });
    setActiveTickets({
      data: returnGroupedTickets(res.data),
      meta: res.meta,
    });
  };

  const fetchHistoryTickets = async (page = 1) => {
    const res = await getTicketsHistory({ page });
    setHistoryTickets({
      data: returnGroupedTickets(res.data),
      meta: res.meta,
    });
  };

  useEffect(() => {
    fetchActiveTickets();
    fetchHistoryTickets();
    if (window?.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'Data from WebView / Website' }));
    }
  }, []);

  const handleTicketRefund = async (ticket, tickets = []) => {
    if (ticket?.type?.toLowerCase() !== 'child') {
      const ticketsHasChild = tickets?.find((t) => t.type?.toLowerCase() === 'child');
      if (ticketsHasChild) {
        const ticketWillHaveAdult = tickets?.find((t) => t.type?.toLowerCase() === 'standard' && t.id !== ticket.id);
        if (!ticketWillHaveAdult) {
          setShowErrorModal(trans('refund_adult_passenger_ticket_first'));
          return;
        }
      }
    }
    try {
      const res = await calculateTicketRefund({
        ride_id: ticket.ride_id,
        seat_number: ticket.seat_number,
        statement_id: ticket.statement.statement_id,
        wagon_number: ticket.wagon_number,
        ticket_id: ticket.id,
      });
      if (res.status === 'success') {
        if (res.data.priceAmount > 0) {
          setRefundModal({
            show: true,
            data: {
              ...res.data,
              ticket,
            },
          });

          setTimeout(() => {
            setRefundModal({
              show: false,
              data: [],
            });
          }, 30000);
        } else {
          setShowErrorModal(trans('refund_not_possible'));
        }
      } else {
        setShowErrorModal(trans('error_try_30m_later'));
      }
    } catch (e) {
      if (e.response.data.status === 'expired') {
        setShowErrorModal(trans('refund_not_possible'));
      }
    }
  };

  const handleDownloadPurchasedTickets = async (ticketsGroup) => {
    handleDownloadTickets(
      ticketsGroup[0].statement?.statement_id,
      ticketsGroup[0].statement?.guid,
    );
  };

  const handleAdditionalServices = (ticket) => {
    setShowTicketAdditionalServicesModal({
      show: true,
      data: {
        ticket_id: ticket.id,
        ride_id: ticket.ride_id,
        seat_number: ticket.seat_number,
        wagon_number: ticket.wagon_number,
        statement_id: ticket.statement.statement_id,
      },
    });
  };

  return (
    <div className="flex flex-col gap-[30px]">
      {searchParams.get('tab') === 'purchased-tickets' && (
        <>
          <PurchasedTicketsList
            title={trans('active_tickets')}
            tickets={activeTickets?.data || {}}
            handleDownloadTicket={handleDownloadPurchasedTickets}
            handleTicketRefund={handleTicketRefund}
            handleAdditionalServices={handleAdditionalServices}
          />
          <DefaultPagination
            currentPage={
                  Number(activeTickets?.meta?.currentPage) || 1
              }
            totalItems={activeTickets?.meta?.totalItems || 0}
            pageSize={activeTickets?.meta?.itemsPerPage || 10}
            onChange={fetchActiveTickets}
          />
          <PurchasedTicketsList
            title={trans('history')}
            tickets={historyTickets?.data || {}}
            expired
            handleDownloadTicket={handleDownloadPurchasedTickets}
            handleAdditionalServices={handleAdditionalServices}
            handleTicketRefund={handleTicketRefund}
          />
          <DefaultPagination
            currentPage={
            Number(historyTickets?.meta?.currentPage) || 1
          }
            totalItems={historyTickets?.meta?.totalItems || 0}
            pageSize={historyTickets?.meta?.itemsPerPage || 10}
            onChange={fetchHistoryTickets}
          />
        </>
      )}
      <TicketRefundModal
        fetchTickets={fetchActiveTickets}
        refundModal={refundModal}
        setRefundModal={setRefundModal}
      />
      <TicketAdditionalServicesModal />
      <RefundConfirmationModal />
    </div>
  );
}

export default PurchasedTickets;
